import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { ChecklistSubsection } from './ChecklistSubsection';
import { ChecklistSection as ChecklistSectionType } from '../../utils/inspectionChecklistData';
import { ItemStatus } from './ChecklistItem';

interface ItemState {
  status: ItemStatus;
  notes?: string;
}

interface ChecklistSectionProps {
  section: ChecklistSectionType;
  itemStates: Record<string, ItemState>;
  onItemChange: (itemId: string, status: ItemStatus, notes?: string) => void;
}

export function ChecklistSection({ section, itemStates, onItemChange }: ChecklistSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      EXTERIOR: 'bg-blue-500',
      'INTERIOR - LIVING AREAS': 'bg-green-500',
      KITCHEN: 'bg-orange-500',
      BEDROOMS: 'bg-pink-500',
      BATHROOMS: 'bg-cyan-500',
      'UTILITIES & SYSTEMS': 'bg-slate-500',
      'SUPPLIES & AMENITIES': 'bg-teal-500',
      LAUNDRY: 'bg-indigo-500',
      'DOCUMENTATION & INFO': 'bg-amber-500',
      'FINAL CHECKS': 'bg-red-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  const getTotalItems = () => {
    return section.subsections.reduce((total, subsection) => total + subsection.items.length, 0);
  };

  const getCheckedItems = () => {
    let checked = 0;
    section.subsections.forEach((subsection) => {
      subsection.items.forEach((item) => {
        if (itemStates[item.id]?.status !== 'unchecked') {
          checked++;
        }
      });
    });
    return checked;
  };

  const total = getTotalItems();
  const checked = getCheckedItems();
  const percentage = total > 0 ? Math.round((checked / total) * 100) : 0;

  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-1 h-8 rounded ${getCategoryColor(section.category)}`}></div>
          <h2 className="text-xl font-bold text-gray-800">{section.category}</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-medium text-gray-700">
              {checked}/{total} items
            </div>
            <div className="text-xs text-gray-500">{percentage}% complete</div>
          </div>
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getCategoryColor(section.category)}`}
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 py-4 bg-gray-50">
          {section.subsections.map((subsection, index) => (
            <ChecklistSubsection
              key={index}
              title={subsection.title}
              items={subsection.items}
              itemStates={itemStates}
              onItemChange={onItemChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
